import { Close } from '@mui/icons-material';
import {
    Box,
    Button,
    DialogTitle,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const FILTER_KEYS = [
    'name',
    'advertiser',
    'broker',
    'status',
    'expiration',
    'type',
    'missingAdvertiser',
    'missingDescription',
    'missingType',
    'orderBy',
];

export function SearchDrawerOffersAdmin({ open, onClose, onClearFilters }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const { values, handleSubmit, handleChange, resetForm } = useFormik({
        initialValues: {
            name: searchParams.get('name') || '',
            advertiser: searchParams.get('advertiser') || '',
            broker: searchParams.get('broker') || '',
            status: searchParams.get('status') || '',
            expiration: searchParams.get('expiration') || '',
            type: searchParams.get('type') || '',
            missingAdvertiser: searchParams.get('missingAdvertiser') || '',
            missingDescription: searchParams.get('missingDescription') || '',
            missingType: searchParams.get('missingType') || '',
            orderBy: searchParams.get('orderBy') || 'updatedAt_desc',
        },
        onSubmit: formValues => {
            const next = {};
            const perPage = searchParams.get('per_page') || searchParams.get('perPage');
            if (perPage) next.per_page = perPage;

            FILTER_KEYS.forEach(key => {
                const value = formValues[key];
                if (key === 'orderBy' && value === 'updatedAt_desc') return;
                if (value !== '' && value != null) next[key] = value;
            });

            setSearchParams(next);
            onClose();
        },
    });

    useEffect(() => {
        resetForm({
            values: {
                name: searchParams.get('name') || '',
                advertiser: searchParams.get('advertiser') || '',
                broker: searchParams.get('broker') || '',
                status: searchParams.get('status') || '',
                expiration: searchParams.get('expiration') || '',
                type: searchParams.get('type') || '',
                missingAdvertiser: searchParams.get('missingAdvertiser') || '',
                missingDescription: searchParams.get('missingDescription') || '',
                missingType: searchParams.get('missingType') || '',
                orderBy: searchParams.get('orderBy') || 'updatedAt_desc',
            },
        });
    }, [searchParams, resetForm]);

    const handleClearFilters = () => {
        onClearFilters();
        resetForm();
    };

    const hasFilters = Object.entries(values).some(([key, value]) => {
        if (key === 'orderBy' && value === 'updatedAt_desc') return false;
        return value !== '' && value != null;
    });

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ padding: 2, width: 360 }}>
                <DialogTitle sx={{ padding: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        Filtrar ofertas
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Nome da oferta"
                            variant="outlined"
                            fullWidth
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                        />
                        <TextField
                            label="Anunciante"
                            placeholder="Nome do anunciante"
                            variant="outlined"
                            fullWidth
                            name="advertiser"
                            value={values.advertiser}
                            onChange={handleChange}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel id="broker-label">Broker</InputLabel>
                            <Select
                                labelId="broker-label"
                                name="broker"
                                value={values.broker}
                                onChange={handleChange}
                                label="Broker"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="awin">Awin</MenuItem>
                                <MenuItem value="lomadee">Lomadee</MenuItem>
                                <MenuItem value="socialsoul">SocialSoul</MenuItem>
                                <MenuItem value="none">Sem broker / local</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="status-label">Status</InputLabel>
                            <Select
                                labelId="status-label"
                                name="status"
                                value={values.status}
                                onChange={handleChange}
                                label="Status"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Ativas</MenuItem>
                                <MenuItem value="false">Inativas</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="expiration-label">Validade</InputLabel>
                            <Select
                                labelId="expiration-label"
                                name="expiration"
                                value={values.expiration}
                                onChange={handleChange}
                                label="Validade"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="valid">Vigentes</MenuItem>
                                <MenuItem value="expired">Expiradas</MenuItem>
                                <MenuItem value="expiring24h">Expirando em 24h</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="type-label">Tipo</InputLabel>
                            <Select
                                labelId="type-label"
                                name="type"
                                value={values.type}
                                onChange={handleChange}
                                label="Tipo"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="cashback">Cashback</MenuItem>
                                <MenuItem value="coupon">Cupom</MenuItem>
                                <MenuItem value="voucher">Voucher</MenuItem>
                                <MenuItem value="none">Sem tipo</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="missingAdvertiser-label">Anunciante vinculado</InputLabel>
                            <Select
                                labelId="missingAdvertiser-label"
                                name="missingAdvertiser"
                                value={values.missingAdvertiser}
                                onChange={handleChange}
                                label="Anunciante vinculado"
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Sem anunciante</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="missingDescription-label">Descrição</InputLabel>
                            <Select
                                labelId="missingDescription-label"
                                name="missingDescription"
                                value={values.missingDescription}
                                onChange={handleChange}
                                label="Descrição"
                            >
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="true">Sem descrição</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel id="orderBy-label">Ordenar por</InputLabel>
                            <Select
                                labelId="orderBy-label"
                                name="orderBy"
                                value={values.orderBy}
                                onChange={handleChange}
                                label="Ordenar por"
                            >
                                <MenuItem value="updatedAt_desc">Atualização (mais recentes)</MenuItem>
                                <MenuItem value="createdAt_desc">Criação (mais recentes)</MenuItem>
                                <MenuItem value="expirationAt_asc">Validade (mais próximas)</MenuItem>
                                <MenuItem value="name_asc">Nome (A–Z)</MenuItem>
                                <MenuItem value="id_desc">ID (mais novos)</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="contained" type="submit">
                            Aplicar
                        </Button>
                        {hasFilters && (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Limpar
                            </Button>
                        )}
                    </Stack>
                </form>
            </Box>
        </Drawer>
    );
}
